import { useContext, useEffect, useMemo, useRef, useState } from 'react';

import { Accordion, Alert, Box, Tag, Text, TextButton, VisuallyHidden } from '@zillow/constellation-10';
import { IconErrorFilled, IconLightBulbFilled } from '@zillow/constellation-icons';
import {
  EnterpriseListingEvent,
  EnterprisePageFeature,
  FeeType,
  RentalManagerEnterpriseEventTrackingWebApiHandler_trackCustomerListingEvent,
  RentalManagerEnterpriseEventTrackingWebApiHandler_trackPageVisit,
  useRentalManagerUserPropertySingleFamilyCostAndFeesWebApiHandler_getFeesForType,
} from '@zg-rentals/rental-manager-env';
import { parseQueryString } from '@zg-rentals/string-utils';

import { FOR_RENT_BY_KEY } from '../../containers/CreateListingPage/constants';

import accessUtils from '../../utils/accessUtils';
import propertyUtils from '../../utils/propertyUtils';
import stringHelper from '../../utils/stringHelper';
import typeCheckUtils from '../../utils/typeCheckUtils';

import { MODAL_COMPONENT } from '../../components/WizardModularListing/Review/constants';
import { contactSectionRequiredFields, requiredFieldNames } from '../../components/WizardModularListing/constants';

import { RMWContext } from '../../providers/RMWContext';
import { useTourSchedulingContext } from '../../components/WizardModularListing/TourSchedulingSetup/context';
import { ModularListingContext } from '../../containers/CreateListingPage/context';

import actions from '../../containers/CreateListingPage/actions';
import { AppSelectors, RoutingSelectors } from '../../selectors';
import { useSelector } from '../../selectors/useSelector';

import StringConstants from '../../constants/StringConstants';
import QueryConstants from '../../constants/QueryConstants';
import { TRIGGER_LOCATIONS } from '../../constants/ClickstreamConstants';
import { REVIEW_PAGE_SECTION_RECS } from '../../constants/RecommendationsConstants';

import ApplicationsCSEvents from '../../model/ClickstreamEvent/ApplicationsCSEvents';

import Modal from '../../components/WizardModularListing/Review/components/Modal';
import AccordionContentContainer from '../AccordionContentContainer';

import RentDetailsContent from '../../components/WizardModularListing/Review/components/RentDetailsContent';
import AmenitiesContent from '../../components/WizardModularListing/Review/components/AmenitiesContent';
import ContactContent from '../../components/WizardModularListing/Review/components/ContactContent';
import CostsAndFeesContent from '../WizardModularListing/Review/components/CostsAndFeesContent';

import MediaContent from '../../components/WizardModularListing/Review/components/MediaContent';

import PropertyInformationContent from '../../components/WizardModularListing/Review/components/PropertyInformationContent';
import ScreeningCriteriaContent from '../WizardModularListing/Review/components/ScreeningCriteriaContent';
import ListingCompletenessScore from '../../components/ListingCompletenessScore';
import { default as EmailVerificationModal } from '../../components/WizardModularListing/ContactInfo/components/EmailFormField';

import type { MouseEvent, ReactNode } from 'react';
import type { CurrentPropertyProps } from '../../model/CurrentProperty';

const IS_RP_CHECK_ENABLED = false;
const SCREENING_CRITERIA_INDEX = '4';

const getModalComponentObj = ({ componentKey, isRoomForRent }: { componentKey: string; isRoomForRent: boolean }) => {
  const renderedComponent = {
    ...MODAL_COMPONENT[componentKey as keyof typeof MODAL_COMPONENT],
  };

  if (isRoomForRent && componentKey === 'PropertyDescriptionFormField') {
    renderedComponent.title = 'Room and shared space description';
  }

  return renderedComponent;
};

const getRequiredRoomForRentFieldErrors = ({
  newRequiredFieldsErrors,
  currentProperty,
}: {
  newRequiredFieldsErrors: Record<string, boolean>;
  currentProperty: CurrentPropertyProps;
}) => {
  const {
    description,
    forRentBy,
    roomForRent: { hasPermissionToCreateListing },
  } = currentProperty;

  // property information check
  if (!description) {
    newRequiredFieldsErrors.floorplans = true;
  }

  // final details check
  if (forRentBy === FOR_RENT_BY_KEY.TENANT && hasPermissionToCreateListing === false) {
    newRequiredFieldsErrors.contact = true;
  }

  return newRequiredFieldsErrors;
};

const getRequiredFieldsErrors = ({
  currentProperty,
  feeAmount,
  canAccessRentalProtectionFromML = false,
  isRoomForRent = false,
  isInModularListingContext = false,
  hasTouringSectionErrors = false,
}: {
  currentProperty: CurrentPropertyProps;
  feeAmount: number | null;
  canAccessRentalProtectionFromML?: boolean;
  isRoomForRent?: boolean;
  isInModularListingContext?: boolean;
  hasTouringSectionErrors?: boolean;
}): Record<string, boolean> => {
  const {
    active,
    amenities,
    floorplans = [],
    isFeed,
    petsAllowed,
    laundry,
    rentalProtection: { isDeferred: isRentalProtectionDeferred, isEnabled: isRentalProtectionEnabled },
  } = currentProperty;

  const { price, beds, baths } = floorplans[0] || {};

  if (active || isFeed) return {};

  const isPetsAllowedEmpty = propertyUtils.getIsPetsAllowedEmpty(petsAllowed);

  let newRequiredFieldsErrors: Record<string, boolean> = {};

  newRequiredFieldsErrors.rentDetails = !price || (isRoomForRent && isPetsAllowedEmpty);
  newRequiredFieldsErrors.screeningCriteria = isPetsAllowedEmpty;

  // contact is the final details section
  newRequiredFieldsErrors.contact = contactSectionRequiredFields.some((field) => {
    if (field === requiredFieldNames.isApplicationsAccepted) {
      // NOTE: currentProperty.isApplicationsAccepted can be `null` due to legacy
      return (
        !isRoomForRent &&
        // @ts-expect-error currentProperty.isApplicationsAccepted
        (currentProperty[field] === null ||
          // @ts-expect-error currentProperty.isApplicationsAccepted
          (!currentProperty[field] && !typeCheckUtils.isNumeric(feeAmount)))
      );
    }

    // @ts-expect-error currentProperty[field] as `field`
    return !currentProperty[field];
  });

  if (hasTouringSectionErrors) {
    newRequiredFieldsErrors.contact = true;
  }

  const isInteriorRequirementFulfilled = propertyUtils.checkIsAmenityFieldFulfilled({
    amenityField: amenities?.interior,
  });
  const isPropertyRequirementFulfilled = propertyUtils.checkIsAmenityFieldFulfilled({
    amenityField: amenities?.property,
  });

  newRequiredFieldsErrors.amenities = !laundry || !isInteriorRequirementFulfilled || !isPropertyRequirementFulfilled;
  newRequiredFieldsErrors.floorplans = beds === '' || !baths;

  // TODO: revisit as rp requirement temporarily disabled
  //https://zillowgroup.slack.com/archives/C01GT0B820N/p1663775385127679?thread_ts=1663346985.294389&cid=C01GT0B820N
  if (
    IS_RP_CHECK_ENABLED &&
    !isInModularListingContext &&
    canAccessRentalProtectionFromML &&
    !isRentalProtectionEnabled
  ) {
    newRequiredFieldsErrors.isRentalProtectionDeferred = !isRentalProtectionDeferred;
  }

  if (isRoomForRent) {
    newRequiredFieldsErrors = getRequiredRoomForRentFieldErrors({
      newRequiredFieldsErrors,
      currentProperty: currentProperty as CurrentPropertyProps,
    });
  }

  return newRequiredFieldsErrors;
};

type Props = {
  setCanPublish: (value: boolean) => void;
  previewListingCTA?: ReactNode;
};

const SFAccordionContainer = ({ setCanPublish, previewListingCTA }: Props): JSX.Element => {
  const { paths } = useContext(RMWContext);

  const {
    modularListingState: {
      canAccessScreeningCriteriaInListingFlow,
      canAccessSFCostsAndFees,
      hasFieldError,
      isInModularListingContext,
      isSaveComplete,
      isSaveDisabled,
      serverErrorObj,
    },
    reducerDispatch,
  } = useContext(ModularListingContext);

  const {
    app: { canAccessRentalProtectionFromML, cookieEnabledServices },
    user,
    user: {
      features: { canUpdateListing },
    },
    properties: {
      recommendations,
      currentProperty,
      currentProperty: {
        active,
        contactEmail,
        dateLastActivatedTimestamp,
        isArchived,
        isFeed,
        listingAlias,
        restrictions: {
          applications: { isUpdateRestricted: isApplicationsUpdateRestricted },
          isUpdateListingRestricted,
        },
        rentalProtection: { isDeferred: isRentalProtectionDeferred },
      },
    },
  } = useSelector((state) => state);
  const isScreenMobile = useSelector((state) => AppSelectors.makeIsScreenMobile(state));
  const search = useSelector((state) => RoutingSelectors.makeSearch(state));

  const [componentKey, setComponentKey] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isOpenWithDelay, setIsOpenWithDelay] = useState(false);
  const [requiredFieldsErrors, setRequiredFieldsErrors] = useState<{ [key: string]: boolean }>({});
  const [expandedAccordionIndices, setExpandedAccordionIndices] = useState<Array<string>>([]);

  const [displayEmailVerification, setDisplayEmailVerification] = useState(false);

  const { data: applicationFee = [] } = useRentalManagerUserPropertySingleFamilyCostAndFeesWebApiHandler_getFeesForType(
    {
      feeType: FeeType.APPLICATION_FEE,
      listingAlias,
    },
    {
      enabled: canAccessSFCostsAndFees && listingAlias !== '',
    },
  );

  const { tourCalendarApiHook, canAccessPostingPathTourScheduling } = useTourSchedulingContext();

  const { availabilityEvents, isTouringSchedulingEnabled } = tourCalendarApiHook;

  const hasTouringSectionErrors =
    canAccessPostingPathTourScheduling && isTouringSchedulingEnabled && !availabilityEvents?.length;

  const feeAmount = applicationFee?.length ? applicationFee?.[0]?.feeAmountMin : null;

  const isRoomForRent = propertyUtils.isRoomForRent({ currentProperty });

  const isDisabled = !canUpdateListing || isArchived || isUpdateListingRestricted;

  const queryParams = parseQueryString(search);

  const prevValuesRef = useRef({
    contactEmail,
  });

  useEffect(() => {
    if (user.customers.length && !user.impersonatorEmail && !user.impersonatorUserToken) {
      RentalManagerEnterpriseEventTrackingWebApiHandler_trackPageVisit({
        page: EnterprisePageFeature.PROPERTY_EDIT,
      }).catch(() => {});
    }
  }, []);

  useEffect(() => {
    if (!currentProperty) {
      return;
    }
    if (user.customers.length && !user.impersonatorEmail && !user.impersonatorUserToken) {
      RentalManagerEnterpriseEventTrackingWebApiHandler_trackCustomerListingEvent({
        feedId: currentProperty.feedId,
        feedListingId: currentProperty.feedListingId,
        event: EnterpriseListingEvent.VIEW_PROPERTY,
      }).catch(() => {});
    }
  }, [currentProperty]);

  useEffect(() => {
    actions.updateShouldSaveOnBlur({ reducerDispatch, shouldSaveOnBlur: false });
    actions.updateHasFieldError({ reducerDispatch, hasFieldError: false });
    setErrorsAndCanPublish();

    return () => actions.updateShouldSaveOnBlur({ reducerDispatch, shouldSaveOnBlur: true });
  }, []);

  useEffect(() => {
    if (isApplicationsUpdateRestricted) {
      removeApplicationRequiredField();
    }
  }, [isApplicationsUpdateRestricted]);

  useEffect(() => {
    if (isInModularListingContext && hasServerError()) {
      setIsSaving(false);

      // fixes issue where saving on blur happens
      // this will keep the manual saving functionality in review edit modal
      actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });

      if (user.customers.length && !user.impersonatorEmail && !user.impersonatorUserToken) {
        RentalManagerEnterpriseEventTrackingWebApiHandler_trackCustomerListingEvent({
          feedId: currentProperty.feedId,
          feedListingId: currentProperty.feedListingId,
          event: EnterpriseListingEvent.EDIT,
        }).catch(() => {});
      }
    }
  }, [isSaveComplete, serverErrorObj.fieldName]);

  useEffect(() => {
    if (hasFieldError === false) {
      setErrorsAndCanPublish();
    }
  }, [isRentalProtectionDeferred]);

  // clicked save cta
  useEffect(() => {
    // isSaveComplete triggers manually saving for the review edit modal state
    setIsSaving(isSaveComplete);

    if (isSaveComplete === false && hasFieldError === false && hasServerError() === false) {
      setErrorsAndCanPublish();
      setIsOpen(false);
    }
  }, [isSaveComplete]);

  useEffect(() => {
    if (prevValuesRef.current.contactEmail === '' && prevValuesRef.current.contactEmail !== contactEmail) {
      prevValuesRef.current.contactEmail = contactEmail;

      setErrorsAndCanPublish();
    }
  }, [contactEmail]);

  useEffect(() => {
    setErrorsAndCanPublish();
  }, [hasTouringSectionErrors]);

  useEffect(() => {}, [isSaveDisabled]);

  useEffect(() => {
    actions.updateIsInModalState({ reducerDispatch, isInModalState: isOpen });

    if (isOpen) {
      // we need to account for possible modal state conditional checks
      setTimeout(() => {
        setIsOpenWithDelay(isOpen);
      }, 0);
    } else {
      setIsOpenWithDelay(isOpen);
      setComponentKey('');
    }
  }, [isOpen]);

  useEffect(() => {
    if (queryParams[QueryConstants.SCROLL_TO] === 'screeningCriteria') {
      setExpandedAccordionIndices([SCREENING_CRITERIA_INDEX]);

      // Wait for accordion to expand before scrolling
      setTimeout(() => {
        const accordionElement = document.querySelector(`[data-accordion-index="${SCREENING_CRITERIA_INDEX}"]`);
        if (accordionElement) {
          accordionElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            container: 'nearest',
          } as ScrollIntoViewOptions);
        }
      }, 300);
    }
  }, [queryParams[QueryConstants.SCROLL_TO]]);

  const {
    component: ModalBodyComponent,
    componentKey: ModalComponentKey,
    title: modalTitle = '',
  } = useMemo(() => getModalComponentObj({ componentKey, isRoomForRent }), [componentKey]);

  const hasServerError = () => {
    return serverErrorObj.fieldName !== '';
  };

  const setErrorsAndCanPublish = () => {
    const newRequiredFieldsErrors = getRequiredFieldsErrors({
      canAccessRentalProtectionFromML,
      isRoomForRent,
      currentProperty,
      isInModularListingContext,
      feeAmount,
      hasTouringSectionErrors,
    });

    setRequiredFieldsErrors(newRequiredFieldsErrors);

    const canPublish = Object.keys(newRequiredFieldsErrors).every((key) => !newRequiredFieldsErrors[key]);
    setCanPublish(canPublish);
  };

  const clearFormError = () => {
    actions.updateHasFieldError({ reducerDispatch, hasFieldError: false });
  };

  const clearSaveDisabled = () => {
    actions.updateAnyProperty({
      reducerDispatch,
      data: {
        isSaveDisabled: false,
      },
    });
  };

  const clearServerError = () => {
    if (serverErrorObj.fieldName) {
      actions.removeServerError({
        reducerDispatch,
      });
    }
  };

  const removeApplicationRequiredField = () => {
    const foundIndex = contactSectionRequiredFields.findIndex((fieldName: string) => {
      return fieldName === requiredFieldNames.isApplicationsAccepted;
    });

    if (foundIndex > -1) {
      contactSectionRequiredFields.splice(foundIndex, 1);
    }
  };

  const trackEditClick = (groupTitle?: string, fieldTitle?: string) => {
    if (!groupTitle || !fieldTitle) {
      return;
    }

    if (fieldTitle.toLowerCase().includes('applications')) {
      ApplicationsCSEvents.ClickEditAcceptApplications({
        currentProperty,
        user,
        triggerLocation: TRIGGER_LOCATIONS.POSTING_PATH_REVIEW,
      });
    }
  };

  const handleModalOpen = () => {
    clearServerError();

    // reset save cta
    actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });
  };

  const handleModalClose = () => {
    clearSaveDisabled();
    clearFormError();
    clearServerError();
    setIsOpen(false);

    actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });
  };

  const handleModalSaveClick = () => {
    if (modalTitle === MODAL_COMPONENT.AmenitiesAdditionalFormField.title) {
      handleModalClose();

      return;
    }
    // communicate that cta has been clicked
    actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: true });
  };

  const handleEditClick = (event: MouseEvent<HTMLButtonElement>) => {
    const { parentElement, value: aComponentKey } = event.currentTarget;

    let fieldTitle = parentElement?.firstChild?.textContent
      ? stringHelper.startCaseRemoveSpace(parentElement.firstChild.textContent)
      : undefined;
    const groupTitle = parentElement?.parentElement?.parentElement?.getAttribute('data-group-title') || undefined;

    if (fieldTitle && fieldTitle.includes(StringConstants.ERROR)) {
      fieldTitle = `Incomplete${fieldTitle.substring(
        fieldTitle.indexOf(StringConstants.ERROR) + StringConstants.ERROR.length,
      )}`;
    }

    trackEditClick(groupTitle, fieldTitle);

    // IncompletePhone comes from the title passed to AccordionIconError and text from Phone
    if (
      fieldTitle === MODAL_COMPONENT.PhoneNumberFormField.title ||
      fieldTitle === `Incomplete${MODAL_COMPONENT.PhoneNumberFormField.title}`
    ) {
      return;
    }

    if (
      fieldTitle === MODAL_COMPONENT.EmailFormField.title ||
      fieldTitle === `Incomplete${MODAL_COMPONENT.EmailFormField.title}`
    ) {
      setDisplayEmailVerification(true);

      return;
    }

    setComponentKey(aComponentKey);
    setIsOpen(true);
  };

  const renderComponent = () => {
    if (!ModalBodyComponent) {
      return <div />;
    }

    return (
      <Box css={{ marginBottom: '1200' }}>
        {/* @ts-expect-error */}
        <ModalBodyComponent closeModal={handleModalClose} />
      </Box>
    );
  };

  const getAccordionTag = ({
    reviewSection,
    isIncomplete = false,
  }: {
    reviewSection: string;
    isIncomplete: boolean;
  }) => {
    // NOTE: recommendations get set via RecommendationsActions.updateRecommendations
    if (Object.keys(recommendations[reviewSection]).length && !isIncomplete) {
      return (
        <Tag tone="blue" icon={<IconLightBulbFilled />} size="sm">
          {isScreenMobile
            ? Object.keys(recommendations[reviewSection]).length
            : `${stringHelper.pluralize(
                Object.keys(recommendations[reviewSection]).length,
                'RECOMMENDATION',
                'RECOMMENDATIONS',
              )}`}
        </Tag>
      );
    }

    return (
      isIncomplete &&
      !isDisabled && (
        <Tag tone="yellow" icon={<IconErrorFilled />} size="sm">
          INCOMPLETE
        </Tag>
      )
    );
  };

  const filteredRequiredFieldsError = useMemo(
    () => Object.values(requiredFieldsErrors).filter((value) => value === true),
    [requiredFieldsErrors],
  );

  const canAccessErrorGuide = accessUtils.canAccessErrorGuide({
    cookieEnabledServices,
  });

  return (
    <div data-testid="SFAccordionContainer">
      <Box css={{ marginBottom: '400' }}>
        {!canAccessErrorGuide && (
          <ListingCompletenessScore
            requiredFieldsErrors={filteredRequiredFieldsError as Array<boolean>}
            previewListingCTA={previewListingCTA}
          />
        )}

        {Boolean(!!dateLastActivatedTimestamp && !isFeed) && (
          <Alert
            css={{ marginTop: '400' }}
            actionButton={
              active && (
                <TextButton textStyle="body-bold" asChild>
                  <a
                    href={`${paths.propertiesPage}/${listingAlias}/tours/tour-calendar`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View or update your availability
                  </a>
                </TextButton>
              )
            }
          >
            {!active ? (
              <Text>After you publish your listing, you&apos;ll be able to set your availability for tours.</Text>
            ) : (
              <Text>
                Zillow&apos;s new tour scheduling feature lets you add your availability to a calendar for easier
                booking.
              </Text>
            )}
          </Alert>
        )}
      </Box>

      <Accordion.Root
        expanded={expandedAccordionIndices}
        onExpandedChange={(values) => {
          setExpandedAccordionIndices(typeof values === 'string' ? [values] : values);
        }}
      >
        <AccordionContentContainer
          tag={getAccordionTag({
            reviewSection: REVIEW_PAGE_SECTION_RECS.PROPERTY_INFORMATION,
            isIncomplete: requiredFieldsErrors.floorplans,
          })}
          header="Property information"
          content={<PropertyInformationContent onEditClick={handleEditClick} />}
          accordionIndex={0}
        />
        <AccordionContentContainer
          tag={getAccordionTag({
            reviewSection: REVIEW_PAGE_SECTION_RECS.RENT_DETAILS,
            isIncomplete: requiredFieldsErrors.rentDetails,
          })}
          header="Rent details"
          content={<RentDetailsContent onEditClick={handleEditClick} />}
          accordionIndex={1}
        />
        <AccordionContentContainer
          tag={getAccordionTag({
            reviewSection: REVIEW_PAGE_SECTION_RECS.MEDIA,
            isIncomplete: requiredFieldsErrors.media,
          })}
          header="Media"
          content={<MediaContent />}
          accordionIndex={2}
        />
        <AccordionContentContainer
          tag={getAccordionTag({
            reviewSection: REVIEW_PAGE_SECTION_RECS.AMENITIES,
            isIncomplete: requiredFieldsErrors.amenities,
          })}
          header="Amenities"
          content={<AmenitiesContent onEditClick={handleEditClick} />}
          accordionIndex={3}
        />

        {canAccessScreeningCriteriaInListingFlow && (
          <AccordionContentContainer
            tag={getAccordionTag({
              reviewSection: REVIEW_PAGE_SECTION_RECS.SCREENING_CRITERIA,
              isIncomplete: requiredFieldsErrors.screeningCriteria,
            })}
            header="Screening criteria"
            content={<ScreeningCriteriaContent onEditClick={handleEditClick} />}
            accordionIndex={Number(SCREENING_CRITERIA_INDEX)}
          />
        )}

        {canAccessSFCostsAndFees && (
          <AccordionContentContainer
            tag={getAccordionTag({
              reviewSection: REVIEW_PAGE_SECTION_RECS.COSTS_AND_FEES,
              isIncomplete: requiredFieldsErrors.costsAndFees,
            })}
            header="Costs and fees"
            content={<CostsAndFeesContent />}
            accordionIndex={5}
          />
        )}

        <AccordionContentContainer
          tag={getAccordionTag({
            reviewSection: REVIEW_PAGE_SECTION_RECS.CONTACT,
            isIncomplete: requiredFieldsErrors.contact,
          })}
          header="Final details"
          content={<ContactContent onEditClick={handleEditClick} tourCalendarHook={tourCalendarApiHook} />}
          accordionIndex={6}
        />
      </Accordion.Root>

      <Modal
        modalTitle={modalTitle}
        isOpen={isOpenWithDelay}
        component={renderComponent()}
        componentKey={ModalComponentKey}
        onOpen={handleModalOpen}
        onClose={handleModalClose}
        onSaveClick={handleModalSaveClick}
        isSaveDisabled={isSaveDisabled}
        isSaving={isSaving}
      />

      <VisuallyHidden>
        {displayEmailVerification && <EmailVerificationModal onClose={() => setDisplayEmailVerification(false)} />}
      </VisuallyHidden>
    </div>
  );
};

export default SFAccordionContainer;
